/**
 * The five scheduling fields a form and a board both talk about — pure, no React, no domain rules.
 *
 * This is the contract between `features/{appointment,task}-upsert` and `widgets/disposition-board`:
 * the form reports what it currently says, the board draws it and reports back what a drag did. It
 * lives in `shared` so neither side has to import the other — `features/disposition-planning` builds
 * one in `draftToDefaultSlot` and takes one apart in `slotToDraft`, and the upsert modals spread it
 * straight over their form values, which is why the keys are the RHF field names.
 *
 * `plannedStart` is the **work** start (`schedule.plannedWorkStartsAt`), never the block start — the
 * trap `appointmentForm.schema.ts` and `slotToDatetimeLocal` both open with. It is an
 * `<input type="datetime-local">` value: local wall clock, no timezone, `YYYY-MM-DDTHH:mm`.
 *
 * **These five fields and nothing else.** Ganztägig was briefly a sixth and had to be taken out
 * again; `onFullDayChange` on the upsert modal is where anything of that kind belongs now. The
 * reason is `sameSlot` below: it gates the board→form write, so every field in this shape is a
 * licence for the board to overwrite the form. The form publishes its slot from a value computed
 * during render while its own effects (`useFullDaySync`) write a render later — so a field that
 * flips *before* the durations it describes makes the first publish differ on that field while still
 * carrying the previous durations. The board latches those, the board→form effect writes them back,
 * and the derivation is undone on a loop. A field that only ever changes *with* the durations is
 * safe; one that announces a change to them is not.
 */
export interface ScheduleSlot {
  assignedTechnicianId: number;
  plannedStart: string;
  plannedTravelDuration: number;
  plannedWorkDuration: number;
  plannedDepartureDuration: number;
}

/**
 * Value equality — the loop guard for the two-way sync.
 *
 * Form → board → form is a cycle: the board hands the slot back to the form it came from. Both ends
 * compare before they write, so the second pass is a no-op and the cycle terminates instead of
 * re-rendering forever.
 *
 * It is also what makes a *stale* publish harmless: the form reports its slot from a value built
 * during render, so the frame in which one of its own effects changes a field still reports the
 * previous one. Identical to what the board already holds, that report is dropped here and the real
 * change arrives on the next frame. See the note on `ScheduleSlot` for what breaks when a field is
 * added that can differ before the durations do.
 */
export function sameSlot(a: ScheduleSlot | null, b: ScheduleSlot | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.assignedTechnicianId === b.assignedTechnicianId &&
    a.plannedStart === b.plannedStart &&
    a.plannedTravelDuration === b.plannedTravelDuration &&
    a.plannedWorkDuration === b.plannedWorkDuration &&
    a.plannedDepartureDuration === b.plannedDepartureDuration
  );
}

/**
 * Which day the slot falls on, or `null` when `plannedStart` is not a datetime-local value.
 *
 * A plain string slice, not a `Date`: `plannedStart` is already local wall clock, and parsing it only
 * to format it back would reintroduce the UTC shift `parseDayIso` exists to avoid.
 */
export function slotDayIso(slot: ScheduleSlot): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}/.exec(slot.plannedStart);
  return match ? match[1] : null;
}
